import { TFunction } from 'i18next';
import { OrderHistory } from '../stores/appOrderHistoryStore';
import { RevenueHistory } from '../services/revenuehistory/revenueHistoryService';
import { CostPriceAnalysis } from '../services/costPrice/costPriceService';
import { CustomIngredient } from '../services/customIngredients/customIngredientsService';
import { endOfMonth, startOfMonth, subDays, subWeeks, subMonths } from 'date-fns';
import { getCategoryDisplayName } from '../components/orderhistory/categoryUtils';
import { getEffectiveMenuPrice, enrichCostPriceWithCurrentPrices, calculateCostPercentage, findLatestMenuPrice, EnrichmentOptions } from './costPriceUtils';
import { formatTimeLabel, TimeOption } from './chartLabels';
import { startOfPeriod as customStartOfPeriod, addPeriod as customAddPeriod } from './dateUtils';
import { isDateInRange, filterOrdersByDateRange, filterRevenueByDateRange } from './dateRangeFilters';
import { calculateTotalRevenue } from './revenueHistoryCalculations';
import { calculateTotalSpent } from './orderHistoryCalculations';
import { calculateCostPercentage as calculateCostPercentageKPI } from './kpiCalculations';
import { getProductPrice } from '../components/orderhistory/vatUtils';
import { getProductQuantity } from './platformUtils';
import { resolveSpendSide } from './spendSideUtils';
import { buildRevenueItemsMap } from './costPriceUtils';
import {
  calculateMenuEngineeringMatrix as calculateMatrixForItem,
  calculateAverageUnitsSold,
  MenuEngineeringMatrix
} from '../services/costPrice/menuEngineeringService';

/**
 * Calculate category spent breakdown for a bar chart
 */
export interface CategorySpentData {
  category: string; // Display name
  categoryKey: string; // Original category key for color mapping
  amount: number;
  percentage: number;
}

export interface FactuurCategorySpentData {
  side: 'food' | 'drinks';
  mainCategory: string;
  categoryKey: string | undefined;
  amount: number;
}

/** Invoice lines are already price-adjusted. Keep credits signed and category identities separate. */
export function calculateFactuurCategorySpentBreakdown(
  orders: OrderHistory[],
  showExVat: boolean,
): FactuurCategorySpentData[] {
  const groups = new Map<string, FactuurCategorySpentData>();
  for (const order of orders) {
    for (const product of order.productData || []) {
      const side = resolveSpendSide(product, order.restaurantSection?.restaurantSectionType,
        order.restaurantSection?.combinedOrderingAccount === true);
      if (side !== 'food' && side !== 'drinks') continue;
      const mainCategory = product.mainCategory || side.toUpperCase();
      const categoryKey = product.category || undefined;
      const key = JSON.stringify([side, mainCategory, categoryKey]);
      const group = groups.get(key) || { side, mainCategory, categoryKey, amount: 0 };
      group.amount += getProductQuantity(product, order.platform) * getProductPrice(product, showExVat);
      groups.set(key, group);
    }
  }
  return Array.from(groups.values()).sort((a, b) => b.amount - a.amount);
}

export function calculateCategorySpentBreakdown(
  orders: OrderHistory[],
  showExVat: boolean,
  translate?: TFunction
): CategorySpentData[] {
  const categoryTotals: Record<string, number> = {};
  let totalSpent = 0;

  // Calculate totals per category (using the category field, not mainCategory)
  orders.forEach(order => {
    (order.productData || []).forEach(product => {
      const category = product.category || 'Unknown';
      const productPrice = getProductPrice(product, showExVat);
      const productQuantity = getProductQuantity(product, order.platform);
      const productTotal = productQuantity * (productPrice || 0);
      categoryTotals[category] = (categoryTotals[category] || 0) + productTotal;
      totalSpent += productTotal;
    });
  });

  // Convert to array and calculate percentages
  const result = Object.entries(categoryTotals).map(([categoryKey, amount]) => ({
    category: getCategoryDisplayName(categoryKey, undefined, translate) || categoryKey,
    categoryKey, // Keep original key for color mapping
    amount,
    percentage: totalSpent > 0 ? (amount / totalSpent) * 100 : 0
  }));

  // Sort by amount descending
  return result.sort((a, b) => b.amount - a.amount);
}

/**
 * Menu Engineering Matrix data point
 */
export interface MenuEngineeringDataPoint {
  id: string;
  name: string;
  costPrice: number;
  menuPrice: number;
  timesSold: number;
  margin: number;
  costPercentage: number;
  marginPercentage: number;
  matrix: MenuEngineeringMatrix;
  categoryId: string | null;
}

/**
 * Determine the best available time window for menu engineering based on available revenue data.
 * Returns the window label ('2months' | '1month' | '1week' | null) and the cutoff date.
 * Prefers the longest window that has data, minimum 1 week.
 */
export function getMenuEngineeringTimeWindow(
  revenueHistory: RevenueHistory[],
  endDate: Date
): { window: '2months' | '1month' | '1week' | null; cutoffDate: Date | null } {
  const twoMonthsAgo = new Date(endDate);
  twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

  const oneMonthAgo = new Date(endDate);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const oneWeekAgo = new Date(endDate);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const hasDataBefore = (date: Date) =>
    revenueHistory.some(rev => new Date(rev.date) <= date);

  if (hasDataBefore(twoMonthsAgo)) {
    return { window: '2months', cutoffDate: twoMonthsAgo };
  }
  if (hasDataBefore(oneMonthAgo)) {
    return { window: '1month', cutoffDate: oneMonthAgo };
  }
  if (hasDataBefore(oneWeekAgo)) {
    return { window: '1week', cutoffDate: oneWeekAgo };
  }
  return { window: null, cutoffDate: null };
}

export function calculateMenuEngineeringMatrix(
  costPriceAnalyses: CostPriceAnalysis[],
  revenueHistory: RevenueHistory[],
  showExVat: boolean,
  endDate?: Date,
  cutoffDate?: Date
): MenuEngineeringDataPoint[] {
  const result: MenuEngineeringDataPoint[] = [];

  // Only include analyses that have both cost and price data
  costPriceAnalyses.forEach(analysis => {
    // Skip if no ingredients (no cost to analyze)
    if (!analysis.ingredients || analysis.ingredients.length === 0) {
      return;
    }

    // Use the same menu price calculation as cost price pages
    const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory);
    if (!effectivePrice) {
      return;
    }

    // Get menu price based on VAT setting (same logic as cost price pages)
    const menuPrice = showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;

    // Skip if no menu price
    if (menuPrice <= 0) {
      return;
    }

    // Calculate times sold using the determined time window (up to endDate)
    const timesSold = calculateTimesSold(analysis.menuItemName || 'no_match', revenueHistory, endDate, cutoffDate);

    // Calculate margin and cost percentage
    const costPrice = analysis.totalCost;
    const margin = menuPrice - costPrice;
    const costPercentage = (costPrice / menuPrice) * 100;
    const marginPercentage = ((menuPrice - costPrice) / menuPrice) * 100;

    result.push({
      id: analysis.id,
      name: analysis.costPriceName,
      costPrice,
      menuPrice,
      timesSold,
      margin,
      costPercentage,
      marginPercentage,
      matrix: null, // Will be calculated after we know the average
      categoryId: analysis.categoryId || null,
    });
  });

  // Convert to DishRowData-like format for average calculation and matrix classification
  // The service functions only use: popularity, totalCost, price, marginPercentage, isPlaceholder
  const itemsForMatrix = result.map(item => ({
    popularity: item.timesSold,
    totalCost: item.costPrice,
    price: item.menuPrice,
    marginPercentage: item.marginPercentage,
    isPlaceholder: false
  })) as any; // Type assertion needed since service expects full DishRowData but only uses these fields

  // Calculate average units sold using the service function
  const averageUnitsSold = calculateAverageUnitsSold(itemsForMatrix);

  // Apply menu engineering matrix classification using the service function
  result.forEach((item, index) => {
    // Use the service function to calculate matrix classification
    item.matrix = calculateMatrixForItem(itemsForMatrix[index], averageUnitsSold);
  });

  return result;
}

function calculateTimesSold(
  itemName: string,
  revenueHistory: RevenueHistory[],
  endDate?: Date,
  cutoffDate?: Date
): number {
  const startDate = cutoffDate || (() => {
    const d = endDate ? new Date(endDate) : new Date();
    d.setMonth(d.getMonth() - 2);
    return d;
  })();

  let totalQuantity = 0;
  const itemNameLower = itemName.toLowerCase();

  revenueHistory.forEach(record => {
    if (isDateInRange(record.date, startDate, endDate || new Date())) {
      record.revenueData.categories.forEach(cat => {
        cat.items.forEach(item => {
          // Use case-insensitive matching for consistency with menu price lookup
          if (item.name && item.name.toLowerCase() === itemNameLower) {
            totalQuantity += item.quantity || 0;
          }
        });
      });
    }
  });

  return totalQuantity;
}

/**
 * Calculate time periods for charts (12 data points: 11 periods back + current)
 */
export interface TimePeriod {
  startDate: Date;
  endDate: Date;
  label: string;
  periodIndex: number; // 0 = most recent, 11 = oldest
}

export function calculateTimePeriods(
  endDate: Date,
  timeOption: TimeOption,
  totalPeriods: number = 12
): TimePeriod[] {
  const periods: TimePeriod[] = [];

  for (let i = 0; i < totalPeriods; i++) {
    let periodStart: Date;
    let periodEnd: Date;
    let labelDate: Date; // Date to use for label formatting

    if (timeOption === 'day') {
      periodEnd = i === 0 ? endDate : subDays(endDate, i);
      periodStart = periodEnd;
      labelDate = periodStart;
    } else if (timeOption === 'week') {
      // Use custom week calculation that matches dateUtils.ts to ensure consistency
      // This calculates weeks as Monday-based, matching the dashboard
      const targetDate = i === 0 ? endDate : subWeeks(endDate, i);
      periodStart = customStartOfPeriod(targetDate, 'week');
      periodEnd = customAddPeriod(periodStart, 'week', 1);
      periodEnd.setDate(periodEnd.getDate() - 1); // End on Sunday (6 days after Monday)
      labelDate = periodStart; // Use start of week for week number calculation
    } else {
      // month, quarter, all
      periodEnd = i === 0 ? endOfMonth(endDate) : endOfMonth(subMonths(endDate, i));
      periodStart = startOfMonth(periodEnd);
      labelDate = periodStart; // Use start of month for label
    }

    periods.push({
      startDate: periodStart,
      endDate: periodEnd,
      label: formatTimeLabel(labelDate, timeOption),
      periodIndex: i
    });
  }

  // Reverse so oldest is first and current period is last (oldest period at index 0, current period at index totalPeriods-1)
  // This ensures charts display left-to-right: oldest period -> ... -> current period
  return periods.reverse();
}

/**
 * Per-period context shared across over-time charts: pre-enriched analyses
 * (so we only pay the price-lookup cost once) and revenue up to the period end
 * (for menu-price lookups).
 *
 * Built from UNFILTERED inputs so the result is stable across overall-type toggles.
 */
export interface PeriodContext {
  period: TimePeriod;
  enrichedById: Map<string, CostPriceAnalysis>;
  revenueUpToPeriod: RevenueHistory[];
}

export function buildPeriodContexts(
  costPriceAnalyses: CostPriceAnalysis[],
  allOrders: OrderHistory[],
  revenueHistory: RevenueHistory[],
  timePeriods: TimePeriod[],
  showExVatOrders: boolean,
  customIngredients?: CustomIngredient[],
  // Cross-restaurant sourcing: sibling data sets keyed by restaurantId. Sibling
  // data is not period-bounded (latest price), unlike the current restaurant's
  // `ordersUpToPeriod` — acceptable for borrowed ingredients (live pricing).
  byRestaurant?: EnrichmentOptions['byRestaurant'],
  currentRestaurantId?: string,
  // Current restaurant's subrecipes, so subrecipe-reference rows are costed
  // (own-restaurant subrecipes resolve from here; siblings from `byRestaurant`).
  allSubrecipes?: EnrichmentOptions['allSubrecipes']
): PeriodContext[] {
  return timePeriods.map(period => {
    const ordersUpToPeriod = filterOrdersByDateRange(allOrders, new Date(0), period.endDate);
    const revenueUpToPeriod = filterRevenueByDateRange(revenueHistory, new Date(0), period.endDate);

    const enrichedById = new Map<string, CostPriceAnalysis>();
    costPriceAnalyses.forEach(analysis => {
      // Enrich with the same period-bounded revenue we'll use for menu-price
      // lookups below — BYO weighting must match the time window of the chart.
      const enriched = enrichCostPriceWithCurrentPrices(analysis, ordersUpToPeriod, {
        customIngredients,
        showExVat: showExVatOrders,
        allCostPriceAnalyses: costPriceAnalyses,
        allSubrecipes,
        revenueHistory: revenueUpToPeriod,
        byRestaurant,
        currentRestaurantId,
      });
      enrichedById.set(analysis.id, enriched);
    });

    return { period, enrichedById, revenueUpToPeriod };
  });
}

/**
 * Cost percentage over time data
 */
export interface CostPercentageTimeData {
  label: string;
  date: Date;
  costPercentage: number; // Cost percentage (inslag)
}

/**
 * Calculate Cost Percentage Over Time
 * Uses timePeriods to calculate cost percentage for each period
 */
export function calculateCostPercentageOverTime(
  orders: OrderHistory[],
  revenueHistory: RevenueHistory[],
  timePeriods: TimePeriod[],
  overallTypeFilter: 'all' | 'food' | 'drinks',
  showExVatOrders: boolean,
  showExVatRevenue: boolean
): CostPercentageTimeData[] {
  const result: CostPercentageTimeData[] = [];

  timePeriods.forEach(period => {
    // Filter orders and revenue for this period
    const periodOrders = filterOrdersByDateRange(orders, period.startDate, period.endDate);
    const periodRevenue = filterRevenueByDateRange(revenueHistory, period.startDate, period.endDate);

    // Calculate total revenue
    const totalRevenue = calculateTotalRevenue(periodRevenue, showExVatRevenue);

    // Calculate total cost - include ALL products to match dashboard calculation
    // The overall type filter is already applied to orders before this function is called
    const totalCost = calculateTotalSpent(periodOrders, showExVatOrders);

    // Calculate cost percentage (inslag)
    const costPercentage = calculateCostPercentageKPI(totalCost, totalRevenue);

    result.push({
      label: period.label,
      date: period.startDate,
      costPercentage
    });
  });

  return result;
}

/**
 * Menu Cost vs Actual Cost data point
 * weighted = theoretical cost % from recipes weighted by per-period quantity sold
 * actual = realized cost % = totalSpent / totalRevenue
 * inefficiency = actual - weighted (waste, portioning, shrinkage, comps, etc.)
 */
export interface MenuCostVsActualData {
  label: string;
  weightedCostPercentage: number | null;
  actualCostPercentage: number | null;
  inefficiency: number | null;
  totalRevenue: number;
  isPartial: boolean;
}

/**
 * Quantity sold per menu item name across a period's revenue, built in ONE pass and then read O(1)
 * per dish — the old helper re-scanned the whole period's revenue for every dish, every period.
 *
 * Every line contributes, including two lines sharing a name inside one category. A POS can bill
 * one product under several article codes (trivecgroup does, and its ids are stripped from the
 * name), so same name = same dish and the quantities add up. This is how every other revenue
 * aggregation already keys — buildRevenueItemsMap, getTopRevenueItems, getByoAggregatesIndex.
 */
export function buildQuantitySoldIndex(periodRevenue: RevenueHistory[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const revenue of periodRevenue) {
    for (const category of revenue.revenueData.categories) {
      for (const item of category.items) {
        if (!item.name) continue;
        totals.set(item.name, (totals.get(item.name) || 0) + (item.quantity || 0));
      }
    }
  }
  return totals;
}

export function calculateMenuCostVsActual(
  costPriceAnalyses: CostPriceAnalysis[],
  typeFilteredOrders: OrderHistory[],
  typeFilteredRevenue: RevenueHistory[],
  periodContexts: PeriodContext[],
  showExVatOrders: boolean,
  showExVatRevenue: boolean
): MenuCostVsActualData[] {
  const now = new Date();
  return periodContexts.map(ctx => {
    const period = ctx.period;
    const periodOrders = filterOrdersByDateRange(typeFilteredOrders, period.startDate, period.endDate);
    const periodRevenue = filterRevenueByDateRange(typeFilteredRevenue, period.startDate, period.endDate);

    const totalCost = calculateTotalSpent(periodOrders, showExVatOrders);
    const totalRevenue = calculateTotalRevenue(periodRevenue, showExVatRevenue);
    const actualCostPercentage = totalRevenue > 0 ? calculateCostPercentageKPI(totalCost, totalRevenue) : null;
    const isPartial = period.endDate > now;

    let weightedNumerator = 0;
    let weightedDenominator = 0;

    const quantitySoldByItem = buildQuantitySoldIndex(periodRevenue);

    costPriceAnalyses.forEach(analysis => {
      if (!analysis.menuItemName) return;

      const enriched = ctx.enrichedById.get(analysis.id);
      if (!enriched || !enriched.totalCost || enriched.totalCost <= 0) return;

      const effectivePrice = getEffectiveMenuPrice(enriched, ctx.revenueUpToPeriod);
      if (!effectivePrice) return;

      const menuPrice = showExVatRevenue ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
      if (menuPrice <= 0) return;

      const quantity = quantitySoldByItem.get(analysis.menuItemName) || 0;
      if (quantity <= 0) return;

      const costPct = calculateCostPercentage(enriched.totalCost, menuPrice);
      weightedNumerator += costPct * quantity;
      weightedDenominator += quantity;
    });

    const weightedCostPercentage = weightedDenominator > 0 ? weightedNumerator / weightedDenominator : null;

    const inefficiency = (actualCostPercentage !== null && weightedCostPercentage !== null)
      ? actualCostPercentage - weightedCostPercentage
      : null;

    return {
      label: period.label,
      weightedCostPercentage,
      actualCostPercentage,
      inefficiency,
      totalRevenue,
      isPartial,
    };
  });
}

/**
 * Revenue vs Inslag data point
 */
export interface RevenueVsInslagData {
  name: string;
  revenue: number;
  inslag: number; // Cost percentage
}

/**
 * Calculate Revenue vs Inslag over time
 */
export function calculateRevenueVsInslag(
  orders: OrderHistory[],
  revenueHistory: RevenueHistory[],
  timePeriods: TimePeriod[],
  overallTypeFilter: 'all' | 'food' | 'drinks',
  showExVatOrders: boolean,
  showExVatRevenue: boolean
): RevenueVsInslagData[] {
  const result: RevenueVsInslagData[] = [];

  timePeriods.forEach(period => {
    // Filter orders and revenue for this period
    const periodOrders = filterOrdersByDateRange(orders, period.startDate, period.endDate);
    const periodRevenue = filterRevenueByDateRange(revenueHistory, period.startDate, period.endDate);

    // Calculate total revenue
    const totalRevenue = calculateTotalRevenue(periodRevenue, showExVatRevenue);

    // Calculate total cost - include ALL products to match dashboard calculation
    // The overall type filter is already applied to orders before this function is called
    const totalCost = calculateTotalSpent(periodOrders, showExVatOrders);

    // Calculate inslag percentage
    const inslag = calculateCostPercentageKPI(totalCost, totalRevenue);

    result.push({
      name: period.label,
      revenue: totalRevenue,
      inslag
    });
  });

  return result;
}


/**
 * Spending Split data point
 */
export interface SpendingSplitData {
  name: string;
  percentage: number;
  value: number;
}

/**
 * Calculate Spending Split
 */
export function calculateSpendingSplit(
  orders: OrderHistory[],
  overallTypeFilter: 'all' | 'food' | 'drinks',
  showExVat: boolean
): SpendingSplitData[] {
  const result: SpendingSplitData[] = [];
  
  let foodSpent = 0;
  let drinksSpent = 0;
  let nonFoodSpent = 0;

  orders.forEach(order => {
    const sectionType = order.restaurantSection?.restaurantSectionType;
    const combined = order.restaurantSection?.combinedOrderingAccount === true;

    // NON_FOOD has no food/drinks side of its own, so under a side filter it stays attached to the
    // connection's section — exactly where it shows today. Resolved through the classifier with an
    // empty product so nothing in this file compares a section type itself.
    const sectionSide = resolveSpendSide({}, sectionType, false);

    (order.productData || []).forEach(product => {
      const productPrice = getProductPrice(product, showExVat);
      const productQuantity = getProductQuantity(product, order.platform);
      const productTotal = productQuantity * (productPrice || 0);

      const side = resolveSpendSide(product, sectionType, combined);

      if (overallTypeFilter === 'all') {
        // Show food, drinks, and non-food separately
        if (side === 'food') {
          foodSpent += productTotal;
        } else if (side === 'drinks') {
          drinksSpent += productTotal;
        } else if (side === 'non-food') {
          nonFoodSpent += productTotal;
        }
      } else if ((overallTypeFilter === 'food' || overallTypeFilter === 'drinks') && side === overallTypeFilter) {
        // The filtered side itself. The explicit food/drinks guard is load-bearing: ChartsPage casts
        // the store's wider OverallTypeFilter down to this signature, so a live 'non-food' (or
        // 'take-away'/'other') value does reach here for the frame before the page resets it. Without
        // the guard that value matched `side === overallTypeFilter` and fell into the drinks bucket.
        if (overallTypeFilter === 'food') {
          foodSpent += productTotal;
        } else {
          drinksSpent += productTotal;
        }
      } else if (side === 'non-food' && sectionSide === overallTypeFilter) {
        // Non-food bought on a connection sitting on the filtered side
        nonFoodSpent += productTotal;
      }
    });
  });

  const totalSpent = foodSpent + drinksSpent + nonFoodSpent;

  if (overallTypeFilter === 'all') {
    if (foodSpent > 0) {
      result.push({
        name: 'Food',
        percentage: totalSpent > 0 ? (foodSpent / totalSpent) * 100 : 0,
        value: foodSpent
      });
    }
    if (drinksSpent > 0) {
      result.push({
        name: 'Drinks',
        percentage: totalSpent > 0 ? (drinksSpent / totalSpent) * 100 : 0,
        value: drinksSpent
      });
    }
    if (nonFoodSpent > 0) {
      result.push({
        name: 'Non-Food',
        percentage: totalSpent > 0 ? (nonFoodSpent / totalSpent) * 100 : 0,
        value: nonFoodSpent
      });
    }
  } else {
    // food or drinks filter
    const mainTypeName = overallTypeFilter === 'food' ? 'Food' : 'Drinks';
    const mainSpent = overallTypeFilter === 'food' ? foodSpent : drinksSpent;
    
    if (mainSpent > 0) {
      result.push({
        name: mainTypeName,
        percentage: totalSpent > 0 ? (mainSpent / totalSpent) * 100 : 0,
        value: mainSpent
      });
    }
    if (nonFoodSpent > 0) {
      result.push({
        name: 'Non-Food',
        percentage: totalSpent > 0 ? (nonFoodSpent / totalSpent) * 100 : 0,
        value: nonFoodSpent
      });
    }
  }

  return result;
}

/**
 * Product Cost % Over Time data point
 */
export interface ProductCostOverTimeData {
  name: string; // Product name
  data: Array<{ period: string; costPercentage: number }>;
}

/**
 * Calculate Product Cost % Over Time
 * Uses shared periodContexts so enrichment is computed once across charts.
 */
export function calculateProductCostPercentageOverTime(
  costPriceAnalyses: CostPriceAnalysis[],
  periodContexts: PeriodContext[],
  revenueHistory: RevenueHistory[],
  showExVatRevenue: boolean
): ProductCostOverTimeData[] {
  const result: ProductCostOverTimeData[] = [];

  costPriceAnalyses.forEach(analysis => {
    if (!analysis.menuItemName) return;
    if (!findLatestMenuPrice(analysis.menuItemName, revenueHistory)) return;

    const productData: Array<{ period: string; costPercentage: number }> = [];

    periodContexts.forEach(ctx => {
      const enrichedAnalysis = ctx.enrichedById.get(analysis.id);
      if (!enrichedAnalysis) {
        productData.push({ period: ctx.period.label, costPercentage: 0 });
        return;
      }

      const effectivePrice = getEffectiveMenuPrice(enrichedAnalysis, ctx.revenueUpToPeriod);
      if (!effectivePrice) {
        productData.push({ period: ctx.period.label, costPercentage: 0 });
        return;
      }

      const menuPrice = showExVatRevenue ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice;
      const costPercentage = calculateCostPercentage(enrichedAnalysis.totalCost, menuPrice);

      productData.push({ period: ctx.period.label, costPercentage });
    });

    if (productData.some(d => d.costPercentage > 0)) {
      result.push({ name: analysis.costPriceName, data: productData });
    }
  });

  return result;
}

/**
 * Sales per Product data point
 */
export interface SalesPerProductData {
  name: string;
  value: number;
}

/**
 * Calculate Sales per Product (current period only)
 */
export function calculateSalesPerProduct(
  revenueHistory: RevenueHistory[],
  showExVat: boolean
): SalesPerProductData[] {
  // Use buildRevenueItemsMap to aggregate revenue by product name
  const revenueItemsMap = buildRevenueItemsMap(revenueHistory, showExVat);

  // Convert map to array format and sort by total revenue descending
  const result: SalesPerProductData[] = Array.from(revenueItemsMap.entries())
    .map(([name, data]) => ({ name, value: data.totalRevenue }))
    .sort((a, b) => b.value - a.value);

  return result;
}
